<template>
  <div class="v-image"
  >
    <div class="v-image__image"
         :class="classes" :style="styles">
      <picture :srcset="srcset">
        <img :src="src" :style="aspectStyle" :alt="alt">
      </picture>
    </div>
  </div>
</template>

<script>

export default {
  name: 'VImg',
  props: {
    alt: String,
    contain: Boolean,
    lazySrc: String,
    aspectRatio: [String, Number],
    position: {
      type: String,
      default: 'center center',
    },
    sizes: String,
    src: {
      type: [String, Object],
      default: '',
    },
    srcset: String,
    width: {
      type: String,
    },
    height: {
      type: String,
    }
  },
  data() {
    return {
      currentSrc: '',
      image: null,
      isLoading: true,
      calculatedAspectRatio: Number,
      naturalWidth: Number,
      hasError: false,
    }
  },
  computed: {
    computedAspectRatio() {
      return Number(this.normalisedSrc.aspect || this.calculatedAspectRatio)
    },
    aspectStyle() {
      return this.computedAspectRatio
        ? {'aspect-ratio': (1 / this.computedAspectRatio) * 100 + '%'}
        : undefined
    },
    normalisedSrc() {
      return this.src && typeof this.src === 'object'
        ? {
          src: this.src.src,
          srcset: this.srcset || this.src.srcset,
          lazySrc: this.lazySrc || this.src.lazySrc,
          aspect: Number(this.aspectRatio || this.src.aspect),
        } : {
          src: this.src,
          srcset: this.srcset,
          lazySrc: this.lazySrc,
          aspect: Number(this.aspectRatio || 0),
        }
    },
    classes() {
      return {
        'v-image__image--preload': this.isLoading,
        'v-image__image--contain': this.contain,
        'v-image__image--cover': !this.contain,
      }
    },
    styles() {
      return {
        'width': !!this.width ? `${this.width}px` : '',
        'min-width': !!this.width ? `${this.width}px` : '',
        'height': !!this.height ? `${this.height}px` : '',
      }
    },
  },
  watch: {
    src() {
      if (!this.isLoading) this.init(undefined, undefined, true)
      else this.loadImage()
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    init(entries) {
      if (this.normalisedSrc.lazySrc) {
        const lazyImg = new Image()
        lazyImg.src = this.normalisedSrc.lazySrc
        this.pollForSize(lazyImg, null)
      }

      if (this.normalisedSrc.src) this.loadImage()
    },
    onLoad() {
      this.getSrc()
      this.isLoading = false
      this.$emit('load', this.src)

      if (
        this.image &&
        (this.normalisedSrc.src.endsWith('.svg') || this.normalisedSrc.src.startsWith('data:image/svg+xml'))
      ) {
        if (this.image.naturalHeight && this.image.naturalWidth) {
          this.naturalWidth = this.image.naturalWidth
          this.calculatedAspectRatio = this.image.naturalWidth / this.image.naturalHeight
        } else {
          this.calculatedAspectRatio = 1
        }
      }
    },
    onError() {
      this.hasError = true
      this.$emit('error', this.src)
    },
    getSrc() {
      if (this.image) this.currentSrc = this.image.currentSrc || this.image.src
    },
    loadImage() {
      const image = new Image()
      this.image = image

      image.onload = () => {
        /* istanbul ignore if */
        if (image.decode) {
          image.decode().catch((err) => {
            console.error(
              `Failed to decode image, trying to render anyway\n\n` +
              `src: ${this.normalisedSrc.src}` +
              (err.message ? `\nOriginal error: ${err.message}` : ''),
              this
            )
          }).then(this.onLoad)
        } else {
          this.onLoad()
        }
      }
      image.onerror = this.onError

      this.hasError = false
      this.sizes && (image.sizes = this.sizes)
      this.normalisedSrc.srcset && (image.srcset = this.normalisedSrc.srcset)
      image.src = this.normalisedSrc.src
      this.$emit('loadstart', this.normalisedSrc.src)

      this.aspectRatio || this.pollForSize(image)
      this.getSrc()
    },
    pollForSize(img, timeout = 100) {
      const poll = () => {
        const {naturalHeight, naturalWidth} = img

        if (naturalHeight || naturalWidth) {
          this.naturalWidth = naturalWidth
          this.calculatedAspectRatio = naturalWidth / naturalHeight
        } else if (!img.complete && this.isLoading && !this.hasError && timeout != null) {
          setTimeout(poll, timeout)
        }
      }

      poll()
    },
  },
};
</script>

<style lang="scss">
@import "VImg";
</style>
